import {
  faCaretDown, faCog, faSignOutAlt, faUserCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'found';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Collapse, Container, Dropdown, DropdownItem, DropdownMenu, DropdownToggle,
  Nav, Navbar, NavbarToggler,
} from 'reactstrap';
import styled from 'styled-components';

import NavigationLogo from 'src/components/Navigation/NavigationLogo';
import { APIConfig } from 'src/config';
import { AvatarItem } from 'src/enosikit/components/Avatar';

const RightNav = styled(Nav).attrs({
  className: 'ms-auto',
})``;

const LeftNav = styled(Nav).attrs({
  className: 'me-auto',
})``;

const AdminNavigationViewerFragment = graphql`
  fragment AdminNavigation_viewer on Viewer @throwOnFieldError {
    id
    viewerUser {
      email
      givenName
      familyName
    }
  }
`;

/**
 * Navigation bar for the admin pages: admin section links and the signed-in user menu.
 * Field errors on the viewer throw to the `AdminLayout` error boundary.
 * @param {object} props - Component props.
 * @param {object} props.viewer - Relay fragment reference for `AdminNavigation_viewer`.
 * @returns {React.ReactElement} AdminNavigation component.
 */
function AdminNavigation({ viewer: viewerRef }) {
  const intl = useIntl();
  const viewer = useFragment(AdminNavigationViewerFragment, viewerRef);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const toggleNavbar = () => setNavbarOpen((prevState) => !prevState);
  const toggleAdmin = () => setAdminDropdownOpen((prevState) => !prevState);
  const toggleProfile = () => setProfileDropdownOpen((prevState) => !prevState);

  const { givenName, familyName, email } = viewer.viewerUser;
  let name = [givenName, familyName].filter(Boolean).join(' ');
  if (name === '') {
    name = email;
  }

  const cobrand = APIConfig().cobrand();
  const primaryBrand = APIConfig().primaryBrand();

  // Admin menu
  const adminToggleLabel = intl.formatMessage({ id: 'navigation.admin_navigation.togle_admin', defaultMessage: 'Admin' });
  const commonHeaderLabel = intl.formatMessage({ id: 'navigation.admin_navigation.dropdown_item.common.header.label', defaultMessage: 'Common' });
  const usersLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.users.link.label', defaultMessage: 'Users' });
  const customerHeaderLabel = intl.formatMessage({ id: 'navigation.admin_navigation.dropdown_item.customer.header.label', defaultMessage: 'Customer' });
  const communitiesLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.communities.link.label', defaultMessage: 'Communities' });
  const portfoliosLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.portfolios.link.label', defaultMessage: 'Portfolios' });
  const propertyUsersLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.property_users.link.label', defaultMessage: 'PropertyUsers' });
  const propertiesLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.properties.link.label', defaultMessage: 'Properties' });
  const primaryBillingPointsLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.primary_billing_points.link.label', defaultMessage: 'Primary Billing Points' });
  const metersLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.meters.link.label', defaultMessage: ' Meters' });
  const standingChargesLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.standing_charges.link.label', defaultMessage: 'Standing Charges' });
  const tariffsLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.tariffs.link.label', defaultMessage: 'Tariffs' });
  const volumeChargesLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.volume_charges.link.label', defaultMessage: 'Volume Charges' });
  const historianHeaderLabel = intl.formatMessage({ id: 'navigation.admin_navigation.dropdown_item.historian.header.label', defaultMessage: 'Historian' });
  const historianSlicesLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.historian_slices.link.label', defaultMessage: 'Slices' });
  const tradesHeaderLabel = intl.formatMessage({ id: 'navigation.admin_navigation.dropdown_item.trades.header.label', defaultMessage: 'Trades' });
  const tradesLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.trades.link.label', defaultMessage: 'Trades' });
  const tradeRulesHeaderLabel = intl.formatMessage({ id: 'navigation.admin_navigation.dropdown_item.trades_rules.header.label', defaultMessage: 'Trade Rules' });
  const tradeRulesLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.trade_rules.link.label', defaultMessage: 'Trade Rules' });

  // Profile menu
  const profileSettingsLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.current_user_settings.link.label', defaultMessage: 'Profile settings' });
  const logOutLinkLabel = intl.formatMessage({ id: 'navigation.admin_navigation.log_out.link.label', defaultMessage: 'Log out' });

  return (
    <Navbar light expand="lg">
      <Container className="d-flex flex-wrap justify-content-between px-0 px-sm-3">
        <Link to="/" className="navbar-brand">
          <NavigationLogo cobrand={cobrand} primaryBrand={primaryBrand} />
        </Link>
        <NavbarToggler onClick={toggleNavbar} />
        <Collapse isOpen={navbarOpen} navbar>
          <LeftNav navbar>
            <Dropdown nav inNavbar isOpen={adminDropdownOpen} toggle={toggleAdmin}>
              <DropdownToggle nav>
                {adminToggleLabel}
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem header>
                  {commonHeaderLabel}
                </DropdownItem>
                <Link to="/admin/users" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {usersLinkLabel}
                </Link>
                <DropdownItem header>
                  {customerHeaderLabel}
                </DropdownItem>
                <Link to="/admin/communities" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {communitiesLinkLabel}
                </Link>
                <Link to="/admin/portfolios" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {portfoliosLinkLabel}
                </Link>
                <Link to="/admin/property-users" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {propertyUsersLinkLabel}
                </Link>
                <Link to="/admin/properties" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {propertiesLinkLabel}
                </Link>
                <Link to="/admin/primary-billing-points" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {primaryBillingPointsLinkLabel}
                </Link>
                <Link to="/admin/meters" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {metersLinkLabel}
                </Link>
                <Link to="/admin/standing-charges" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {standingChargesLinkLabel}
                </Link>
                <Link to="/admin/tariffs" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {tariffsLinkLabel}
                </Link>
                <Link to="/admin/volume-charges" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {volumeChargesLinkLabel}
                </Link>
                <DropdownItem divider />
                <DropdownItem header>
                  {historianHeaderLabel}
                </DropdownItem>
                <Link to="/admin/historian/slices" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {historianSlicesLinkLabel}
                </Link>
                <DropdownItem divider />
                <DropdownItem header>
                  {tradesHeaderLabel}
                </DropdownItem>
                <Link to="/admin/trades" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {tradesLinkLabel}
                </Link>
                <DropdownItem divider />
                <DropdownItem header>
                  {tradeRulesHeaderLabel}
                </DropdownItem>
                <Link to="/admin/trade-rules" className="dropdown-item" role="menuitem" onClick={toggleAdmin}>
                  {tradeRulesLinkLabel}
                </Link>
              </DropdownMenu>
            </Dropdown>
          </LeftNav>
          <RightNav navbar>
            <Dropdown nav inNavbar isOpen={profileDropdownOpen} toggle={toggleProfile}>
              <DropdownToggle nav>
                <AvatarItem
                  primaryText={name}
                  secondaryText={email}
                  avatarImage={<FontAwesomeIcon icon={faUserCircle} size="3x" />}
                  className="d-inline-flex"
                />
                <FontAwesomeIcon icon={faCaretDown} size="1x" className="ms-2" />
              </DropdownToggle>
              <DropdownMenu end>
                <Link to="/profile/settings" activeClassName="active" className="dropdown-item" role="menuitem">
                  <FontAwesomeIcon icon={faCog} className="me-2" />
                  {profileSettingsLinkLabel}
                </Link>
                <DropdownItem divider />
                <Link to="/logout" activeClassName="active" className="dropdown-item" role="menuitem">
                  <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                  {logOutLinkLabel}
                </Link>
              </DropdownMenu>
            </Dropdown>
          </RightNav>
        </Collapse>
      </Container>
    </Navbar>
  );
}

AdminNavigation.propTypes = {
  viewer: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default AdminNavigation;
